import { useEffect, useRef, useState } from "react";

// Force Reanimated initialization before any components mount
import { preventAutoHideAsync } from "expo-splash-screen";
import { startMapper, stopMapper } from "react-native-reanimated";

import { DefaultStackScreenOptions, ScreenPath } from "@/constants/Navigation";
import { Stack } from "@/crossPlatform/expo-router";
import {
  initializeLaunchDarkly,
  LDWrapper,
} from "@/crossPlatform/LaunchDarkly";
import useAppFonts from "@/hooks/useAppFonts";
import { useGlobalAppProviders } from "@/hooks/useAppProviders";
import useAuth from "@/hooks/useAuth";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { captureMessage, initAppMonitor, logInfo } from "@/services/AppMonitor";
import ColdStartTracker, { ColdStartPhase } from "@/services/ColdStartTracker";
import logDeviceAttributes from "@/services/DeviceAttributesLogger";
import { useCanLoggedInUserSeeAppContentSelector } from "@/store/reducers/appStartup";
import RootNavigator from "@/ui/components/navigation/RootNavigator";
import { toMs } from "@/utils/date.util";
import { isWeb } from "@/utils/platform.util";

// Workaround for Reanimated 4.4.0 with New Architecture:
// global.__mapperRun is only defined when createMapperRegistry() runs,
// which happens lazily on first startMapper() call. Tamagui Input's focusStyle
// tries to register event handlers that call __mapperRun before it exists,
// causing crashes on the Login screen (first screen rendered).
// Force initialization here to ensure __mapperRun is defined before any TextField mounts.
// Issue: https://github.com/software-mansion/react-native-reanimated/issues/9524
// Fix PR: https://github.com/software-mansion/react-native-reanimated/pull/9573
// TODO: Remove this workaround after upgrading to Reanimated version with PR #9573 merged
try {
  const dummyMapperId = startMapper(() => {
    "worklet";
    // No-op worklet to trigger mapper initialization
  });
  stopMapper(dummyMapperId);
} catch {
  // Ignore errors if mapper initialization fails
}

// Mark app module load time
const ModuleLoadTime = performance.now();
ColdStartTracker.markModuleLoad(ModuleLoadTime);

// Defer Datadog initialization to avoid blocking initial render
// It will be initialized during the first useEffect in RootLayout
const appMonitorInit = () => {
  const initStart = performance.now();
  initAppMonitor(() => {
    ColdStartTracker.mark(ColdStartPhase.DatadogInitialized);
    logInfo({
      message: `[Startup] Datadog init: ${(performance.now() - initStart).toFixed(1)}ms`,
    });

    void logDeviceAttributes();
  });
};

// the expo-splash-screen is the only thing displayed in the app until it is explicitly hidden.
// this ensures that, during initial app startup, while the app is still initializing and not
// yet ready, a splash screen will be shown rather than a blank white screen.
//
// expo-splash-screen is configured in app.config.js:splash
//
// ALL other splash screen uses (ie not app startup) are provided via ui/components/splashScreen/SplashScreenProvider
void preventAutoHideAsync();

// LaunchDarkly has a default 5 second timeout, so we add a second for buffer to be safe
const InitializationTimeoutMs = toMs({ seconds: 6 });

const ProtectedStack = () => {
  const { isAuthenticated, isLoading, preLoginCleanup } = useAuth();
  const canLoggedInUserSeeAppContent =
    useCanLoggedInUserSeeAppContentSelector();

  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    const onColdStart = async () => {
      if (!isWeb()) {
        await preLoginCleanup();
      }
      setIsInitialized(true);
    };
    if (isInitialized || isLoading) return;
    void onColdStart();
  }, [isInitialized, isLoading, preLoginCleanup]);

  if (!isWeb() && !isInitialized) return null;

  return (
    <RootNavigator>
      <Stack
        initialRouteName={ScreenPath.Login}
        screenOptions={DefaultStackScreenOptions}
      >
        <Stack.Protected guard={!isAuthenticated}>
          <Stack.Screen name={ScreenPath.Login} />
        </Stack.Protected>
        <Stack.Protected guard={isAuthenticated}>
          <Stack.Protected guard={!canLoggedInUserSeeAppContent}>
            <Stack.Screen name={ScreenPath.PostLoginRouting} />
          </Stack.Protected>
          <Stack.Protected guard={canLoggedInUserSeeAppContent}>
            <Stack.Screen name={ScreenPath.App} />
          </Stack.Protected>
          <Stack.Screen name={ScreenPath.WebAuth0Callback} />
        </Stack.Protected>
        <Stack.Screen name={ScreenPath.Logout} />
      </Stack>
    </RootNavigator>
  );
};

const RootLayout = () => {
  // ANY re-render of RootLayout, once it's no longer returning null, can cause duplicated network request issues on login
  // Thus we avoid adding any additional useState variables that may change after no longer returning null (such as isTimeoutExceeded)
  // See: https://teamequip.atlassian.net/browse/JN-1907

  logInfo({
    message: `[Startup] RootLayout render: ${(performance.now() - ModuleLoadTime).toFixed(1)}ms from module load`,
  });

  const { didLoadFontsSuccessfully } = useAppFonts();
  const didLoadFontsSuccessfullyRef = useRef<boolean>(didLoadFontsSuccessfully);
  useEffect(() => {
    didLoadFontsSuccessfullyRef.current = didLoadFontsSuccessfully;
    if (didLoadFontsSuccessfully) {
      ColdStartTracker.mark(ColdStartPhase.FontsLoaded);
      logInfo({
        message: `[Startup] Fonts loaded: ${(performance.now() - ModuleLoadTime).toFixed(1)}ms from module load`,
      });
    }
  }, [didLoadFontsSuccessfully]);

  const [isLaunchDarklyReady, setIsLaunchDarklyReady] =
    useState<boolean>(false);
  const isLaunchDarklyReadyRef = useRef<boolean>(isLaunchDarklyReady);
  useEffect(() => {
    isLaunchDarklyReadyRef.current = isLaunchDarklyReady;
  }, [isLaunchDarklyReady]);

  const { getDeviceInfo } = useDeviceInfo();

  // Start device info gathering immediately (parallelize with font loading)
  const deviceInfoPromiseRef =
    useRef<Promise<Awaited<ReturnType<typeof getDeviceInfo>>>>(getDeviceInfo());

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const areFontsReady = didLoadFontsSuccessfullyRef.current;
      const isLDReady = isLaunchDarklyReadyRef.current;
      if (areFontsReady && isLDReady) return;

      captureMessage(
        `App layout not ready to render after ${InitializationTimeoutMs}ms. Fonts status: ${areFontsReady} - LaunchDarkly status: ${isLDReady}`,
      );

      // if, somehow, LD is not ready after the initialization timeout, emit this message once and move on
      // this may seem redundant given the finally clause around initializeLaunchDarkly
      // however, it seems the LD library may not reliably enforce its 5 second timeout and may freeze sometimes
      // we still keep that finally clause since, on success, that updates isLaunchDarklyReady much sooner
      if (!isLDReady) {
        setIsLaunchDarklyReady(true);
      }
    }, InitializationTimeoutMs);

    // cleanup function on unmount, to prevent potential memory leaks
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    // Ensure Datadog initialization runs in background (non-blocking)
    appMonitorInit();

    // regardless of success or failure, do not proceed with rendering the app
    // until launch darkly has finished initialization (or its default 5s timeout)
    const initializeLaunchDarklyWithDeviceInfo = async () => {
      const ldStart = performance.now();

      // Use pre-fetched device info to avoid waiting
      const deviceInfo = await deviceInfoPromiseRef.current;

      logInfo({
        message: `[Startup] Device info ready: ${(performance.now() - ldStart).toFixed(1)}ms`,
      });

      try {
        await initializeLaunchDarkly({
          appVersion: deviceInfo.appVersion,
          key: deviceInfo.deviceId,
          manufacturer: deviceInfo.manufacturer,
          modelName: deviceInfo.modelName,
          osName: deviceInfo.osName,
          osVersion: deviceInfo.osVersion,
        });
        logInfo({
          message: `[Startup] LaunchDarkly init: ${(performance.now() - ldStart).toFixed(1)}ms`,
        });
      } finally {
        // even if initialization fails, set this to true so that the app can proceed
        // with rendering itself (and just falling back to launchDarkly flag defaults)
        // rather than freezing the entire app indefinitely because LD initialization failed
        setIsLaunchDarklyReady(true);
        ColdStartTracker.mark(ColdStartPhase.LaunchDarklyReady);

        logInfo({
          message: `[Startup] Ready to render: ${(performance.now() - ModuleLoadTime).toFixed(1)}ms from module load`,
        });
      }
    };

    void initializeLaunchDarklyWithDeviceInfo();
  }, []);

  const AppProviders = useGlobalAppProviders();

  // if fonts are not ready, however, we should block the app from starting up, as fonts are a hard pre-requisite
  // if LD is not ready, then once the initialization timeout is exceeded, we forcibly set isLaunchDarklyReady to true to just move on
  if (!didLoadFontsSuccessfully || !isLaunchDarklyReady) {
    // cannot return anything here, like a LandingPage component, other than null,
    // as this is before <AppProviders> and thus before tamagui are setup/ready to use
    // hence why the AppStartupSplashScreen is displayed by default until explicitly disabled
    return null;
  }

  return (
    <AppProviders>
      <ProtectedStack />
    </AppProviders>
  );
};

// eslint-disable-next-line import-x/no-anonymous-default-export, import-x/no-unused-modules -- export needed for side effect
export default LDWrapper(RootLayout);
